<template>
    <div class="tw:w-full" :class="props.customClass">
        <ValidationProvider
            :name="props.label || props.name"
            :rules="computedRules"
            :value="localValue"
            v-slot="{ errors }"
            mode="eager"
            ref="provider"
        >
            <div
                class="tw:relative tw:w-full"
                :data-validation-error="errors[0] ? '' : undefined"
            >
                <input
                    type="datetime-local"
                    :id="props.id"
                    :data-cy="props.dataCy || props.name"
                    :value="localValue"
                    :min="toLocal(min) || undefined"
                    :max="toLocal(max) || undefined"
                    :disabled="disabled"
                    class="tw:block tw:p-2 tw:pr-10 tw:w-full tw:text-sm tw:rounded tw:border tw:appearance-none tw:focus:outline-none tw:peer"
                    :class="errors[0]
                        ? 'tw:border-red-500 tw:bg-red-50/50 tw:focus:border-blue-400'
                        : 'tw:border-slate-300 tw:bg-white tw:focus:border-blue-400'"
                    @input="onInput"
                />
                <!-- Fallback calendar icon. Safari/WebKit does not render the
                     native `::-webkit-calendar-picker-indicator` on date/time
                     inputs, so we overlay our own there. It stays hidden in
                     Chromium/Firefox (which do paint the native one) to avoid a
                     duplicated icon — see the scoped CSS below. -->
                <i
                    v-if="!disabled"
                    class="fas fa-calendar-alt date-picker-fallback tw:absolute tw:right-3 tw:top-1/2 tw:-translate-y-1/2 tw:text-slate-400 tw:pointer-events-none"
                    aria-hidden="true"
                ></i>
            </div>
            <span
                v-if="errors[0]"
                class="tw:block tw:mt-1 tw:text-xs tw:text-red-600 tw:font-medium"
            >
                {{ formatErrorMessage(errors[0]) }}
            </span>
            <span
                v-else-if="props.apiValidationError"
                class="tw:block tw:mt-1 tw:text-xs tw:text-red-600 tw:font-medium"
            >
                {{ Array.isArray(props.apiValidationError) ? props.apiValidationError.join(', ') : props.apiValidationError }}
            </span>
        </ValidationProvider>
    </div>
</template>

<script setup>
    import { ref, computed, watch } from 'vue';

    const props = defineProps({
        // v-model contract (Vue 2.7 Composition API)
        modelValue: { type: String, default: null },
        // legacy contract (:value + @input)
        value: { type: String, default: null },
        label: String,
        name: String,
        id: String,
        dataCy: String,
        rules: [String, Object],
        min: { type: String, default: null },
        max: { type: String, default: null },
        disabled: Boolean,
        apiValidationError: [String, Array],
        customClass: String
    });

    const emit = defineEmits(['update:modelValue', 'input']);

    const computedRules = computed(() => props.rules || '');

    // Strip timezone offset ("+02:00", "Z", "-05:00"), swap any space
    // separator for "T", and truncate to "YYYY-MM-DDTHH:mm" so the value
    // matches <input type="datetime-local"> (its min/max attributes reject
    // anything else, e.g. the backend's "Y-m-d H:i" format).
    const toLocal = v => {
        if (v == null) return '';
        const str = String(v);
        if (!str) return '';
        return str
            .replace(/[+-]\d{2}:\d{2}$/, '')
            .replace(/Z$/, '')
            .replace(/^(\d{4}-\d{2}-\d{2}) /, '$1T')
            .slice(0, 16);
    };

    // Internal mirror so :value is bound to a ref and the watcher can sync
    // when the parent pushes a new modelValue (e.g. reset on submit error).
    const localValue = ref(toLocal(props.modelValue ?? props.value));

    const formatErrorMessage = err => {
        const msg = typeof err === 'string' ? err : (err && err.message) || String(err || '');
        return msg ? msg.charAt(0).toUpperCase() + msg.slice(1).toLowerCase() : '';
    };

    function onInput(e) {
        const val = e.target.value;
        localValue.value = val;
        // Emit both contracts: v-model listeners use update:modelValue,
        // legacy callers listen to input.
        emit('update:modelValue', val);
        emit('input', val);
    }

    // Sync localValue when the parent pushes a new value via either contract
    // (e.g. submit reset, reactive update from a watcher in the parent).
    watch([() => props.modelValue, () => props.value], ([mv, v]) => {
        const incoming = toLocal(mv ?? v);
        if (incoming !== localValue.value) {
            localValue.value = incoming;
        }
    });
</script>

<script>
    export default {
        inheritAttrs: false
    };
</script>

<style scoped>
    /* The native calendar indicator is painted by Chromium and Firefox on
       `type="datetime-local"`, so the fallback icon must stay hidden there.
       Safari/WebKit does NOT render it, so `@supports (font:
       -apple-system-body)` — an Apple-only feature query — re-enables our
       icon exactly there. Because the two icons are never shown at the same
       time, the control always shows a single calendar icon on every browser. */
    .date-picker-fallback {
        display: none;
    }

    @supports (font: -apple-system-body) {
        .date-picker-fallback {
            display: block;
        }
    }
</style>